import { Reveal } from "@components/ui";

interface ServiceCard {
  number: string;
  title: string;
  subtitle: string;
  bullets: string[];
  note?: React.ReactNode;
  bulletColor: string;
  icon: React.ReactNode;
}

const serviceCards: ServiceCard[] = [
  {
    number: "01",
    title: "New Gas Connection",
    subtitle: "We provide a simple and fast connection process",
    bullets: [
      "Submit online application",
      "Upload documents (EJARI / Title Deed)",
      "Meter activation",
      "Start receiving gas supply",
    ],
    note: <span>Smart meter connection: <strong>AED 25</strong> (one-time)</span>,
    bulletColor: "#122d63",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    number: "02",
    title: "Gas Supply & Consumption",
    subtitle: "We supply LPG/SNG for:",
    bullets: ["Kitchens", "Gas cookers", "Gas ovens", "Villas with tank systems"],
    note: (
      <span>
        Your monthly bill is based on consumption at <strong>AED 35 per m³</strong>.
      </span>
    ),
    bulletColor: "#f03813",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M12 2C8 6 4 9.58 4 14a8 8 0 0016 0c0-4.42-4-8-8-12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 22a4 4 0 004-4c0-2.21-2-4-4-6-2 2-4 3.79-4 6a4 4 0 004 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "03",
    title: "Monthly Billing & Online Payments",
    subtitle: "Convenient payment options:",
    bullets: [
      "Bills issued every month",
      "Pay online via portal",
      "Auto-reminders sent by SMS & email",
      "Access up to 12-month consumption history",
    ],
    note: (
      <span>
        Available payment methods: Visa / Mastercard, Online banking, Direct portal payment
      </span>
    ),
    bulletColor: "#0a7c3e",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M2 10h20M6 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    number: "04",
    title: "Safety Inspections & Annual Checks",
    subtitle: "GAZ conducts:",
    bullets: [
      "Annual safety inspection",
      "Leak detection",
      "Regulator check",
      "Flame quality & ventilation check",
      "Mechanical room access check (if applicable)",
    ],
    note: (
      <span>
        Customers receive a <strong>digital safety report</strong>.
      </span>
    ),
    bulletColor: "#7c3aed",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "05",
    title: "Emergency Response (24/7 Hotline)",
    subtitle: "Our certified technicians are available 24/7 to:",
    bullets: [
      "Shut off gas in emergency",
      "Detect leaks",
      "Repair issues",
      "Restore safe operation",
    ],
    note: <span>Hotline: <strong>800-GAZ</strong></span>,
    bulletColor: "#ea580c",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "06",
    title: "Meter Reading & Consumption Tracking",
    subtitle: "Stay on top of your gas usage:",
    bullets: [
      "Smart meters → automatic.",
      "Mechanical meters → read monthly or submitted by customer.",
      "Consumption history available in your portal",
    ],
    bulletColor: "#0891b2",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export function ResidentialServicesDetailSection() {
  return (
    <section className="w-full bg-white overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-8 md:gap-12 lg:gap-16">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Our Services
            </span>
            <h2 className="text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Services for Residential Customers
            </h2>
            <p className="max-w-[966px] text-[14px] md:text-base lg:text-[28px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
              Comprehensive gas solutions tailored to your home needs
            </p>
          </div>
        </Reveal>

        {/* Service Cards Grid */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {serviceCards.map((card, index) => (
            <Reveal key={card.title} direction="up" delay={index * 80} className="flex">
              <div className="flex-1 rounded-[12px] md:rounded-[24px] bg-white overflow-hidden p-4 md:py-[22px] md:px-[23px] flex flex-col gap-4 md:gap-8 group hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-[rgba(98,99,108,0.15)] hover:border-[#e8e9ed]">
                {/* Number Badge + Title */}
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 md:h-14 md:w-14 rounded-[8px] md:rounded-[12px] overflow-hidden shrink-0 flex items-center justify-center bg-[#122d63] group-hover:bg-[#f03813] transition-colors duration-300"
                  >
                    <span className="text-[18px] md:text-[24px] font-semibold text-white font-albert-sans">
                      {card.number}
                    </span>
                  </div>
                  <h3 className="text-[18px] md:text-[24px] tracking-[-0.04em] font-semibold text-[#20262b] font-albert-sans leading-[1.2] flex-1">
                    {card.title}
                  </h3>
                </div>

                {/* Content */}
                <div className="flex flex-col gap-2 md:gap-3">
                  <p className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] leading-[26px] text-[rgba(10,14,46,0.75)] font-albert-sans">
                    {card.subtitle}
                  </p>
                  <div className="flex flex-col gap-2 md:gap-3">
                    {card.bullets.map((bullet) => (
                      <div key={bullet} className="flex items-start gap-2.5">
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                          style={{ backgroundColor: `${card.bulletColor}26` }}
                        >
                          <svg viewBox="0 0 12 12" className="w-3 h-3">
                            <path d="M10 3L4.5 8.5 2 6" stroke={card.bulletColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                          </svg>
                        </div>
                        <span className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] leading-[26px] text-[rgba(10,14,46,0.75)] font-albert-sans">
                          {bullet}
                        </span>
                      </div>
                    ))}
                  </div>
                  {card.note && (
                    <p className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] leading-[26px] text-[rgba(10,14,46,0.75)] font-albert-sans mt-1">
                      {card.note}
                    </p>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
